import React, { useEffect, useRef } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from '../BasicElement';
import Button, { ILink } from '../Button';
import Tab from '../Tab';
import Tabs from '../Tabs';

export interface IVHSMegaTabsItems {
	/**
	 * Link
	 */
	link?: ILink;
	/**
	 * Sublabel
	 */
	sublabel?: string;
}
export interface IVHSMegaTabs {
	/**
	 * Link
	 */
	link?: ILink;
	/**
	 * Items
	 */
	items?: IVHSMegaTabsItems[];
}
export interface IVHSMegaTabsContent {
	/**
	 * Icon
	 */
	icon?: 'fast-track' | 'search';
	/**
	 * Heading
	 */
	heading?: string;
	/**
	 * Text
	 */
	text?: string;
	/**
	 * Button
	 */
	button?: ILink;
}
export interface IVHSMegaTabsProps extends IBaseElementProps {
	/**
	 * Tabs
	 */
	tabs?: IVHSMegaTabs[];
	/**
	 * Content
	 */
	content?: IVHSMegaTabsContent;
	/**
	 * Zero based.  First tab is index 0
	 */
	defaultActiveTab?: number;
	/**
	 * CTA
	 */
	cta?: ILink;
}

/**
 * Mega Tabs
 * @block
 * @icon images-alt
 */
const VHSMegaTabs: React.FC<IVHSMegaTabsProps> = ({
	tabs = [],
	content,
	defaultActiveTab,
	cta,
	className,
	classNames = [],
}) => {
	const handleClick = (e) => {
		e.target
			.closest('.dropdown-menu.mega-menu.show')
			.classList.remove('show');
	};

	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!ref.current) {
			return;
		}

		const handleTabAccessibility = (event: any) => {
			const tabItems = ref.current?.querySelectorAll(
				'.vhs-mega-tabs__tabs .nav-item',
			) as NodeListOf<HTMLElement>;
			tabItems && tabItems.forEach((item) => {
				item.removeAttribute('role');
			});

			const buttons = ref.current?.querySelectorAll(
				'.vhs-mega-tabs__tabs .nav-link a',
			) as NodeListOf<HTMLElement>;
			buttons && buttons.forEach((button) => {
				button.parentElement.setAttribute('tabindex', '-1');
			});

			const tabs = document.querySelector(
				'.vhs-mega-tabs__tabs .nav-tabs',
			) as HTMLDivElement;

			const listItem = document.createElement('li');
			listItem.innerHTML = `<a href="${cta.href}" class="vhs-mega-tabs__cta btn btn-sm btn-tertiary">All Virtual High School Courses</a>`;
			tabs.appendChild(listItem);
		};

		const document = window.document;
		window.addEventListener('load', handleTabAccessibility);

		return () => {
			window.removeEventListener('load', handleTabAccessibility);
		};
	}, []);

	return (
		<div className={className || cn('vhs-mega-tabs', ...classNames)} ref={ref}>
			<div className="vhs-mega-tabs__container">
				<div className="vhs-mega-tabs__nav">
					{tabs && tabs?.length > 0 && (
						<Tabs
							className="vhs-mega-tabs__tabs"
							defaultActiveTab={defaultActiveTab}
						>
							{tabs.map(({ link, items }, index) => (
								<Tab
									// Component being updated to in tbk-component to add support for React elements to be added here
									// @ts-ignore
									title={
										link?.href ? (
											<a
												href={link?.href}
												onClick={(e) => {
													handleClick(e);
												}}
											>
												{link?.label}
											</a>
										) : (
											<span>{link?.label}</span>
										)
									}
									key={index}
									id={`mega-tabs-tab-${index}`}
								>
									{items && items?.length > 0 && (
										<ul>
											{items.map(
												(
													{ link, sublabel },
													index2,
												) => (
													<li key={index2}>
														<a
															href={link.href}
															target={
																link?.target
															}
															rel={
																link?.target ===
																'_blank'
																	? 'noopener noreferrer'
																	: ''
															}
															key={index2}
														>
															<div className="vhs-mega-tabs__label">
																{link.label}
															</div>
															<div className="vhs-mega-tabs__sublabel">
																{sublabel}
															</div>
														</a>
													</li>
												),
											)}
										</ul>
									)}
								</Tab>
							))}
						</Tabs>
					)}
				</div>
				<div className="vhs-mega-tabs__content">
					{content?.icon && (
						<i
							className={`vhs-mega-tabs__icon icon icon-${content.icon}`}
						></i>
					)}
					{content?.heading && (
						<div className="vhs-mega-tabs__heading">
							{content.heading}
						</div>
					)}
					{content?.text && (
						<div className="vhs-mega-tabs__text">
							{content.text}
						</div>
					)}
					{content?.button?.label && (
						<Button
							{...content.button}
							className="vhs-mega-tabs__button btn btn-sm btn-secondary"
						/>
					)}
				</div>
			</div>
		</div>
	);
};

export default VHSMegaTabs;
